import { useEffect, useState, type ReactNode } from 'react';
import { useRouter } from 'next/router';
import { Globe } from 'lucide-react';
import { useTranslation } from '../../../lib/i18n';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { SPACE_PRESENCE_CARD_COMPONENT, spacePresenceBiFields } from '../../../lib/space-presence-bi';
import {
  presenceCandidate,
  presenceLinks,
  presenceNextStep,
  presenceVisible,
  type PresenceAttention,
  type PresenceLink,
} from '../../../lib/presence';
import { updateSpaceBinding, useSpaceBindings } from '../../../lib/space-bindings';
import { spaceBindingUpdateError } from '../../../lib/space-resources';
import { useIsPhone } from '../../../lib/use-is-phone';
import { useBasePath } from '../../../lib/use-base-path';
import { useSitePaneFits } from '../../../lib/use-site-pane';
import { siteRefOf, useSiteEditRunner } from '../site-review/use-site-edit';
import { cardPresence, headerPresence, presenceStrip, type PresenceAction } from '../../../lib/presence-checklist';
import { useCalendarGate } from '../../../lib/calendar-flag';
import { siteAnalyticsPath } from '../../../lib/site-analytics';
import { siteDomainPath, sitePlansPath } from '../../../lib/site-plans';
import { connectorsPath } from '../../../lib/connectors-route';
import { spaceCardFor, type SpaceCardAction, type SpaceCardChip } from '../../../lib/space-card';
import { useSpaceCardEnabled } from '../../../lib/space-card-flag';
import { useQuery } from '@tanstack/react-query';
import { fetchConnectors } from '../../../lib/connectors';
import { queryKeys } from '../../../lib/query-client';
import { useMe } from '../../../lib/me';
import { presentWebsite, type WorkspaceWebsite } from '../../../lib/workspace-website';
import type { WorkspaceSummary } from '../../../lib/workspaces';
import { Button, Card, TextButton, Toast, Typography } from '../ui';
import { presenceActionChat } from './PresenceChecklist';
import { PresenceGlyph } from './PresenceGlyph';
import { PresenceManage } from './PresenceManage';
import { SiteStrip } from './SiteStrip';
import { SpaceCard } from './SpaceCard';
import { WorkspaceBadge } from '../WorkspaceBadge';
import { usePresenceChat, usePresenceLabel } from './use-presence-chat';
import styles from './SpacePresence.module.css';

// The Space's online presence, in its header (design Option C): under the title, a quiet line — the main website as
// a globe + host, one small round mark per other presence (a dot when it needs attention), then "Manage"; on the
// right, ONE next-step pill; and, for a suggested website, one quiet "Is {name} this Space's website? Yes · Not this
// one" line under the header. It replaced the Space "site bar" card. Personal, and any Space with no presence, gets
// nothing at all.
//
// Every rule is `lib/presence.ts` (unit-tested, mirrored by iOS `Presence.swift` and Android `Presence.kt`); this file
// only draws it. Manage opens `PresenceManage`.
//
// When the server describes the Space (`headerPresence`: `WorkspaceSummary.presence`, else the older
// `WorkspaceWebsite.presence`), the header is the SITE STRIP instead (`SiteStrip`, rules `presenceStrip` in
// lib/presence-checklist.ts): Site · Plan · Domain · Business email · Visits · On Wix cells with one accent, then an
// outlined Manage that opens the Online presence card. Without `presence` (an older server) it is the line, pill and
// Manage above.
//
// With `symphonySpaceCardV2` on, the strip is the SPACE CARD instead (`SpaceCard`, rules `spaceCard` in lib/space-card.ts):
// also for Personal and for a Space with only social profiles, which get no strip today. The card's "Manage Online
// Presence" button opens the same window (under that title). A Space with a website the server did not describe keeps
// the line.

function Dot({ attention }: { attention: PresenceAttention }) {
  const tone = attention === 'RECONNECT' || attention === 'NOT_OWNED' ? styles.dotCritical : styles.dotWarning;
  return <span className={`${styles.dot} ${tone}`} aria-hidden />;
}

/**
 * The presence's three pieces, for the Space header to place: `line` under the title, `pill` in the title row's
 * actions slot, `candidate` under the header — each null when it has nothing to say. `strip` marks the site strip,
 * carried in `pill` and drawn as its own row under the header (never in the title row, the Agents island's corner). They appear together once the
 * bindings read settles, so the header never changes shape after it lands.
 */
export function useSpacePresence(
  workspace: WorkspaceSummary | undefined,
  pageName: string,
): { line: ReactNode; pill: ReactNode; candidate: ReactNode; strip?: boolean } {
  const { t } = useTranslation();
  const { logPageClickBi, logItemSeen } = useGenericBi();
  const bindings = useSpaceBindings(workspace?.id);
  const workspaceId = workspace?.id ?? '';
  const workspaceName = workspace?.name ?? '';
  const openChat = usePresenceChat(workspaceId, workspaceName);
  const labelOf = usePresenceLabel();
  const [manageOpen, setManageOpen] = useState(false);
  /** The Manage Online Presence window opened from the card's "+ Add": its add-link field starts open. */
  const [manageAdding, setManageAdding] = useState(false);
  /** The profile a social card shows: the chip the reader picked; null = the first. */
  const [selectedProfile, setSelectedProfile] = useState<string | null>(null);
  const spaceCardOn = useSpaceCardEnabled();
  // The Personal card's account rows: the signed-in email, how many apps are connected, and whether a calendar is —
  // read only for a Space with no website and no presence (the card that shows them), on the connectors key every
  // reader shares.
  const me = useMe();
  const calendarEnabled = useCalendarGate().enabled;
  const personalCard = spaceCardOn && !!workspace && !workspace.presence && !workspace.website;
  const connectors = useQuery({ queryKey: queryKeys.connectors, queryFn: fetchConnectors, staleTime: 60_000, enabled: personalCard });
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const phone = useIsPhone();
  const router = useRouter();
  const basePath = useBasePath();
  const paneFits = useSitePaneFits();
  const website0 = workspace ? presentWebsite(workspace.website) : null;
  // The site the offers' chats are about — the SAME as the header's chats below (the tile's, else the main link's), so a
  // MOVE offer's seed carries exactly the context the REBUILD step's chat does.
  const main0 = website0 ? null : presenceLinks(website0, bindings.data).find((l) => l.main);
  const offerSite: WorkspaceWebsite | null = website0 ?? (main0 ? { name: main0.name, url: main0.url } : null);
  const runSiteEdit = useSiteEditRunner({ workspaceId, workspaceName, website: offerSite, siteRef: siteRefOf(offerSite) });

  // The presence card shown, with the state it offers (lib/space-presence-bi) — once per state while it is on screen.
  const biState =
    workspace && !bindings.isLoading && (headerPresence(workspace) || presenceVisible(website0, bindings.data))
      ? spacePresenceBiFields(presenceNextStep(website0, bindings.data)).state
      : null;
  useEffect(() => {
    if (biState) logItemSeen(SPACE_PRESENCE_CARD_COMPONENT, { additionalFields: { state: biState, platform: 'web' } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [biState]);

  const none = { line: null, pill: null, candidate: null };
  if (!workspace || bindings.isLoading) return none;
  const website = presentWebsite(workspace.website);
  const data = bindings.data;
  const links = presenceLinks(website, data);
  const mainLinkUrl = links.find((l) => l.main)?.url ?? '';
  const ctx = { spaceName: workspaceName, siteUrl: mainLinkUrl, phone, paneFits };
  const connected = connectors.data?.connectors.filter((c) => c.connected);
  const account = {
    email: me?.email ?? '',
    connectedApps: connected ? connected.length : null,
    calendarConnected: connected ? connected.some((c) => /calendar/i.test(c.toolkit)) : null,
    calendarEnabled,
  };
  // Only once the bindings are read: a failed read is not an empty Space, so it keeps today's strip rather than
  // drawing a Personal card. The card's presence is the bindings' own (`cardPresence`), the Space list's as the fallback.
  const card =
    spaceCardOn && data ? spaceCardFor(cardPresence(data, headerPresence(workspace)), website, data, ctx, account, selectedProfile) : null;
  const header = card ? null : presenceStrip(headerPresence(workspace), ctx);
  if (!card && !header && !presenceVisible(website, data)) return none;

  const click = (button: string) => logPageClickBi({ page_name: pageName, button_name: button });
  const openManage = () => {
    click('presence_manage');
    setManageOpen(true);
  };
  const step = presenceNextStep(website, data);
  const suggested = presenceCandidate(website, data);

  const answer = async (update: 'CONFIRM' | 'DISMISS', bindingId: string) => {
    if (busy) return;
    click(update === 'CONFIRM' ? 'presence_confirm_site' : 'presence_dismiss_site');
    setBusy(true);
    try {
      const error = spaceBindingUpdateError(await updateSpaceBinding({ workspaceId, action: update, bindingId }));
      if (error) setToast(t(error));
    } finally {
      setBusy(false);
    }
  };

  const main = links.find((l) => l.main);
  const others = links.filter((l) => !l.main);


  // A mark with no public page (a site not live yet, a profile with no link) opens Manage instead.
  const mainLink = main ? (
    main.url ? (
      <a
        key="main"
        href={main.url}
        target="_blank"
        rel="noopener noreferrer"
        className={`${styles.mainLink} sym-p3`}
        aria-label={main.attention ? labelOf({ ...main, providerName: '' }) : undefined}
        data-testid="spacePresenceMain"
        onClick={() => click('presence_link_open')}
      >
        <Globe size={14} aria-hidden />
        <span className={styles.host}>{main.name}</span>
        {main.attention && <Dot attention={main.attention} />}
      </a>
    ) : (
      <TextButton
        key="main"
        size="small"
        className={styles.mainButton}
        startIcon={<Globe size={14} aria-hidden />}
        aria-label={labelOf({ ...main, providerName: '' })}
        data-testid="spacePresenceMain"
        onClick={openManage}
      >
        <span className={styles.host}>{main.name}</span>
        {main.attention && <Dot attention={main.attention} />}
      </TextButton>
    )
  ) : null;

  const mark = (link: PresenceLink) => {
    const inner = (
      <>
        <PresenceGlyph glyph={link.glyph} size={14} />
        {link.attention && <Dot attention={link.attention} />}
      </>
    );
    return link.url ? (
      <a
        key={link.id}
        href={link.url}
        target="_blank"
        rel="noopener noreferrer"
        className={styles.mark}
        aria-label={labelOf(link)}
        title={labelOf(link)}
        data-testid={`spacePresenceLink-${link.id}`}
        onClick={() => click('presence_link_open')}
      >
        {inner}
      </a>
    ) : (
      <button
        key={link.id}
        type="button"
        className={styles.mark}
        aria-label={labelOf(link)}
        title={labelOf(link)}
        data-testid={`spacePresenceLink-${link.id}`}
        onClick={openManage}
      >
        {inner}
      </button>
    );
  };

  const line =
    links.length > 0 ? (
      <div className={styles.line} data-testid="spacePresenceLine">
        {mainLink}
        {main && others.length > 0 && (
          <span className={styles.separator} aria-hidden>
            ·
          </span>
        )}
        {others.map(mark)}
        {data && (
          <TextButton size="small" className={styles.manage} data-testid="spacePresenceManage" onClick={openManage}>
            {t('workspace.siteBar.manage')}
          </TextButton>
        )}
        {data && (
          <PresenceManage
            open={manageOpen}
            onClose={() => setManageOpen(false)}
            workspaceId={workspaceId}
            workspaceName={workspaceName}
            website={website}
            bindings={data}
            pageName={pageName}
          />
        )}
        <Toast open={toast !== null} message={toast ?? ''} severity="error" onClose={() => setToast(null)} />
      </div>
    ) : null;

  const pill = step ? (
    <Card variant="outline" size="sm" disablePadding className={styles.pill} data-testid="spacePresenceNextStep" data-step={step.kind}>
      <Typography variant="p3" color="primary" component="span" className={styles.pillText}>
        {t(step.text.key, step.text.values)}
      </Typography>
      <Button
        color="accent"
        size="small"
        onClick={() => {
          click(step.bi);
          openChat(step.chat);
        }}
      >
        {t(step.actionKey)}
      </Button>
    </Card>
  ) : null;

  const candidate = suggested ? (
    <div className={styles.candidate} data-testid="spacePresenceCandidate">
      <Typography variant="p3" color="secondary" component="span">
        {t('workspace.siteBar.candidateQuestion', { name: suggested.name })}
      </Typography>
      <TextButton size="small" className={styles.manage} disabled={busy} onClick={() => void answer('CONFIRM', suggested.bindingId)}>
        {t('workspace.siteBar.confirmYes')}
      </TextButton>
      <span className={styles.separator} aria-hidden>
        ·
      </span>
      <TextButton size="small" className={styles.manage} disabled={busy} onClick={() => void answer('DISMISS', suggested.bindingId)}>
        {t('workspace.siteBar.notThisOne')}
      </TextButton>
      {/* With no line above it, the answer's error toast and Manage live here. */}
      {links.length === 0 && !header && <Toast open={toast !== null} message={toast ?? ''} severity="error" onClose={() => setToast(null)} />}
    </div>
  ) : null;

  if (header || card) {
    // The site a header chat is about: the tile's, else the main link's; none on a Space with no website yet.
    const chatSite: WorkspaceWebsite | null = website ?? (main ? { name: main.name, url: main.url } : null);
    const go = (path: string) => void router.push(path, `${basePath}${path}`);
    const act = (a: PresenceAction | SpaceCardAction) => {
      click(a.bi);
      // The Space card's own destinations (spec 2026-10-29): Connectors, the Space's Apps / Calendar, Manage.
      switch (a.target.kind) {
        case 'connectors':
          go(connectorsPath({ target: a.target.connector || undefined }));
          return;
        case 'apps':
          go(`/workspace/${encodeURIComponent(workspace.slug)}/apps`);
          return;
        case 'calendar':
          go(`/workspace/${encodeURIComponent(workspace.slug)}/calendar`);
          return;
        case 'manage':
          setManageAdding(a.target.add);
          setManageOpen(true);
          return;
      }
      const presenceAction = a as PresenceAction;
      if (runSiteEdit(presenceAction)) return;
      // The Visits tile: the Space's Analytics page, in Symphony (spec 2026-10-25 §5) — never Wix.
      if (a.target.kind === 'analytics') {
        const path = siteAnalyticsPath(workspace.slug);
        void router.push(path, `${basePath}${path}`);
        return;
      }
      // Upgrade and Get <domain>: the in-app Plans / Domain pages (spec 2026-10-25, plan-domain) — never Wix.
      if (a.target.kind === 'plans' || a.target.kind === 'domain') {
        const path = a.target.kind === 'plans' ? sitePlansPath(workspace.slug) : siteDomainPath(workspace.slug);
        void router.push(path, `${basePath}${path}`);
        return;
      }
      if (a.target.kind === 'chat') openChat(presenceActionChat(a.target.message, chatSite));
    };
    const openSetup = (add: boolean, bi: string) => {
      click(bi);
      setManageAdding(add);
      setManageOpen(true);
    };
    const onChip = (chip: SpaceCardChip) => {
      click('space_card_chip');
      // A profile with no public page opens Manage Online Presence, where its row lives.
      if (!chip.url) {
        setManageAdding(false);
        setManageOpen(true);
      }
    };
    const onSelectProfile = (chip: SpaceCardChip) => {
      click('space_card_chip_select');
      setSelectedProfile(chip.id);
    };
    const strip = (
      <>
        {card ? (
          <SpaceCard
            card={card}
            badge={<WorkspaceBadge ws={workspace} size={56} />}
            onAct={act}
            onManage={data ? openSetup : undefined}
            onFooter={() => {
              click('space_card_footer_connectors');
              go(connectorsPath());
            }}
            onChip={onChip}
            onSelectProfile={onSelectProfile}
          />
        ) : (
          header && <SiteStrip strip={header} onAct={act} onManage={data ? openManage : undefined} />
        )}
        {data && (
          <PresenceManage
            open={manageOpen}
            onClose={() => setManageOpen(false)}
            workspaceId={workspaceId}
            workspaceName={workspaceName}
            website={website}
            bindings={data}
            pageName={pageName}
            spacePresence={headerPresence(workspace)}
            spaceCard={card !== null}
            startAdding={manageAdding}
          />
        )}
        <Toast open={toast !== null} message={toast ?? ''} severity="error" onClose={() => setToast(null)} />
      </>
    );
    return { line: null, pill: strip, candidate, strip: true };
  }

  return { line, pill, candidate };
}
